import { Directive, ElementRef, Host, Input, OnDestroy, OnInit } from "@angular/core";
import { fromEvent, Subscription } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { AIOverlayControlDirective } from "./overlay-control.directive";

@Directive({
    selector: "[deloitteaiOverlayTrigger]",
    providers: [{ provide: "trigger", useValue: true }],
})
export class AIOverlayTriggerDirective implements OnDestroy, OnInit {
    /**
     * can be added to component to use as custom trigger
     * set overlay to be triggered by 'click', 'hover' or 'focus'
     * if not set defaults to 'click'
     */

    @Input()
    public aiOverlayTrigger: "click" | "hover" | "focus";

    private eventSubscription: Subscription;

    constructor(
        @Host() private overlayCtrl: AIOverlayControlDirective,
        private el: ElementRef<HTMLElement>
    ) { }

    /**
     * view gets initialized
     *
     */
    ngOnInit(): void {
        switch (this.aiOverlayTrigger) {
            case "focus":
                this.registerFocusListener();
                break;

            case "hover":
                this.registerHoverListener();
                break;

            default:
                this.registerClickListener();
        }
    }

    /**
     * component gets destroyed
     *
     */
    ngOnDestroy(): void {
        this.eventSubscription.unsubscribe();
        this.eventSubscription = null;
    }

    /**
     * register for mouseclick
     *
     */
    private registerClickListener(): void {
        const click$ = fromEvent(this.el.nativeElement, "click");
        this.eventSubscription = click$.subscribe(() =>
            this.overlayCtrl.toggleOverlay()
        );
    }

    /**
     * register mouseover stream
     *
     */
    private registerHoverListener(): void {
        const mouseover$ = fromEvent(this.el.nativeElement, "mouseover");
        const mouseout$ = fromEvent(this.el.nativeElement, "mouseout");

        this.eventSubscription = mouseover$
            .pipe(
                tap(() => this.overlayCtrl.openOverlay()),
                switchMap(() => mouseout$)
            )
            .subscribe(() => this.overlayCtrl.closeOverlay());
    }

    /**
     * register focus / blur listener
     *
     */
    private registerFocusListener(): void {
        const focus$ = fromEvent(this.el.nativeElement, "focus");
        const blur$ = fromEvent(this.el.nativeElement, "blur");

        this.eventSubscription = focus$
            .pipe(
                tap(() => this.overlayCtrl.openOverlay()),
                switchMap(() => blur$)
            )
            .subscribe(() => this.overlayCtrl.closeOverlay());
    }
}
